import { components } from "@appretio/api";
import { useCallback } from "react";
import type {
  FetchForecastDataParams,
  ForecastGridDataOutput
} from "../useFetchForecastData";
import { useRestateFPOS } from "../useRestateFPOS";
import type {
  AppliedCubeFilters,
  WorkspaceTableState
} from "./useWorkspaceTableState";

/** Inputs for {@link useHandleRestateFPOS}. */
interface UseHandleRestateFPOSOptions {
  /** Cube id the restate targets. ``undefined`` short-circuits. */
  saveCubeId: string | undefined;
  /** Filters used by the most recent Apply click. */
  appliedFilters: AppliedCubeFilters;
  /** Imperative forecast-data fetcher (invoked after a successful restate). */
  fetchForecastData: (
    params: FetchForecastDataParams
  ) => Promise<ForecastGridDataOutput | null>;
  /** Table state setters owned by ``useWorkspaceTableState``. */
  tableState: Pick<
    WorkspaceTableState,
    "setRestateFPOSDialogOpen" | "setToastState"
  >;
}

/**
 * Memoised Restate-FPOS handler extracted from ``useWorkspaceActions``.
 *
 * Mirrors ``handleSave``'s success flow: on successful restate, fire a
 * Toast in the shared success channel so the user sees feedback from
 * the same UI surface regardless of which action they triggered. The
 * follow-up grid refetch routes its errors through the global
 * ``MutationCache`` toast (``useFetchForecastData`` passes
 * ``meta.errorMessage``).
 */
export function useHandleRestateFPOS({
  saveCubeId,
  appliedFilters,
  fetchForecastData,
  tableState
}: UseHandleRestateFPOSOptions) {
  const restateFPOS = useRestateFPOS();
  const { setRestateFPOSDialogOpen, setToastState } = tableState;

  const handleRestateFPOS = useCallback(async () => {
    if (!saveCubeId) return false;
    await restateFPOS.mutateAsync({
      context:
        appliedFilters.context as components["schemas"]["ForecastContextOptions-Input"],
      cube_id: saveCubeId
    });
    setRestateFPOSDialogOpen(false);
    setToastState({
      open: true,
      variant: "success",
      title: "Restate successful",
      message: "Forward period of sales restated"
    });
    // Named, or a second own cube among the references leaves the reload
    // with no writable producer, behind a Save that is still enabled.
    fetchForecastData({
      context: appliedFilters.context,
      cubeIds: appliedFilters.cubeIds,
      saveCubeId
    }).catch(() => {});
    return false;
  }, [
    saveCubeId,
    restateFPOS,
    appliedFilters,
    fetchForecastData,
    setRestateFPOSDialogOpen,
    setToastState
  ]);

  return {
    handleRestateFPOS,
    restateFPOSPending: restateFPOS.isPending
  };
}
